import { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLogin } from '@/hooks/useAuth';
import { useGuardianLogin } from '@/hooks/useGuardian';
import { loginErrorMessage } from '@/lib/errors';
import DiggerLogo from '@/components/DiggerLogo';
import PasswordInput from '@/components/PasswordInput';

export default function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [searchParams] = useSearchParams();
  // Diggers sign in with their ID or email; Encarregados de Educação with
  // their email, against their own accounts.
  const [account, setAccount] = useState<'digger' | 'ee'>(
    searchParams.get('conta') === 'ee' ? 'ee' : 'digger',
  );
  const diggerLogin = useLogin();
  const guardianLogin = useGuardianLogin();
  const login = account === 'ee' ? guardianLogin : diggerLogin;
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const onSuccess = () => navigate(account === 'ee' ? '/' : from, { replace: true });
    if (account === 'ee') {
      guardianLogin.mutate({ email: identifier.trim(), password }, { onSuccess });
      return;
    }
    diggerLogin.mutate({ identifier: identifier.trim(), password }, { onSuccess });
  };

  return (
    <>
      <Helmet>
        <title>Iniciar sessão | AICD</title>
      </Helmet>
      <div className="auth-splash min-h-screen flex flex-col px-7 py-10">
        <span
          className="phone-bg-blob phone-bg-blob--accent"
          style={{ width: 280, height: 280, top: -80, right: -60 }}
          aria-hidden="true"
        />
        <span
          className="phone-bg-blob phone-bg-blob--green"
          style={{ width: 220, height: 220, bottom: 120, left: -40 }}
          aria-hidden="true"
        />

        <div className="relative z-10 mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
          <div className="flex flex-col items-center">
            <div className="logo-mark mb-5">
              <DiggerLogo className="h-[108px] w-[108px]" />
            </div>
            <h1 className="brand-title text-white">
              DIGGER <span className="accent-cdi">CDI</span> APP
            </h1>
            <p className="brand-tagline">
              Acesso a equipamento e reservas
            </p>
          </div>

          <div
            role="group"
            aria-label="Tipo de conta"
            className="mt-10 grid grid-cols-2 gap-1 rounded-2xl bg-white/10 p-1"
          >
            {(
              [
                ['digger', 'Digger'],
                ['ee', 'Encarregado de Educação'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={account === value}
                onClick={() => {
                  setAccount(value);
                  diggerLogin.reset();
                  guardianLogin.reset();
                }}
                className={`rounded-xl px-2 py-2 text-xs font-bold transition ${
                  account === value ? 'bg-white text-brand-purple' : 'text-white/85'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <form
            className="mt-6 flex flex-col gap-4"
            onSubmit={handleSubmit}
            aria-busy={login.isPending}
          >
            <div role="alert" aria-live="assertive" aria-atomic="true">
              {login.isError && (
                <div
                  id="login-error"
                  className="rounded-xl bg-red-500/15 border border-red-300/30 px-4 py-3 text-sm font-medium text-red-100"
                >
                  {loginErrorMessage(login.error)}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="identifier" className="label">
                {account === 'ee' ? 'Email' : 'ID ou Email'}
              </label>
              <input
                id="identifier"
                name="identifier"
                type={account === 'ee' ? 'email' : 'text'}
                inputMode={account === 'ee' ? 'email' : 'text'}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                placeholder={account === 'ee' ? 'nome@exemplo.com' : '000000 ou nome@exemplo.com'}
                className="input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                aria-invalid={login.isError || undefined}
                aria-describedby={login.isError ? 'login-error' : undefined}
              />
            </div>

            <div>
              <div className="flex items-baseline justify-between">
                <label htmlFor="password" className="label">
                  Palavra-passe
                </label>
                <Link
                  to={account === 'ee' ? '/forgot-password?conta=ee' : '/forgot-password'}
                  className="text-xs font-semibold text-white/85 hover:text-white"
                >
                  Esqueceu-se da palavra-passe?
                </Link>
              </div>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                required
                placeholder="Introduza a sua palavra-passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={login.isError || undefined}
                aria-describedby={login.isError ? 'login-error' : undefined}
              />
            </div>

            <button
              type="submit"
              disabled={login.isPending}
              className="btn-primary w-full mt-2"
            >
              {login.isPending ? 'A iniciar sessão…' : 'Iniciar sessão'}
            </button>

            <div className="divider-or my-1">ou</div>

            <Link
              to={account === 'ee' ? '/register/ee' : '/register'}
              className="btn-outline w-full text-center"
            >
              {account === 'ee' ? 'Pedir conta de Encarregado' : 'Criar nova conta'}
            </Link>
          </form>
          <p className="mt-6 text-center text-xs text-muted-foreground">
            <Link to="/privacidade" className="underline underline-offset-2 hover:text-foreground">
              Privacidade: como tratamos os seus dados
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}
